import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  HeadContent,
  MakeRouteMatchUnion,
  Outlet,
  createRootRouteWithContext,
  useRouterState,
} from "@tanstack/react-router";
import type { RouterContext } from "../routerContext";

import { PostHogProvider, usePostHog } from "@posthog/react";
import posthog from "posthog-js";
import Layout from "../components/Layout";
import DomainContextProvider, { useDomain } from "../contexts/domain-provider";
import { getDomain, sharePage } from "../data/data-layer";
import ErrorPage from "../pages/ErrorPage";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { roomTypeGetRoomTypes } from "../gen/backend.gen";
import { ratesLinkForRoomType } from "../services/BookingConstants";

dayjs.extend(customParseFormat);
import { NotFoundComponent } from "../components/NotFoundComponent";

import HeadManager from "../services/HeadManager";
import { ServicesSSR } from "../services/ServicesManager";
import { useEffect } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { DebugOverlayProvider } from "../contexts/debug-overlay";

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context: { queryClient, domainName } }) => {
    const domain = await queryClient
      .fetchQuery(getDomain(domainName))
      .then((d) => d.data?.[0]);
    return {
      domain: domain,
      title: domain?.title,
      metadata: domain?.metadata,
    };
  },
  head: (ctx) => {
    return HeadManager.routerHead(ctx);
  },
  component: import.meta.env.SSR ? ServerRootComponent : ClientRootComponent,
  errorComponent: import.meta.env.SSR
    ? ServerErrorComponent
    : ClientErrorComponent,
  notFoundComponent: NotFoundComponent,
});

function getStaticData(matches: MakeRouteMatchUnion[]) {
  return matches
    .map((m) => m.staticData)
    .reduce((acc: Record<string, any>, obj: Record<string, any>) => {
      Object.keys(obj).map((k) => (acc[k] = obj[k]));
      return acc;
    }, {});
}

async function ServerRootComponent() {
  const { domainName, domain, queryClient } = Route.useRouteContext();
  const matches = useRouterState({ select: (s) => s.matches });
  const title = HeadManager.getTitle(matches);
  const staticData = getStaticData(matches);
  const style = await HeadManager.getCss(domainName);

  return (
    <html lang="en" data-theme="light">
      <head>
        <HeadContent />
        <style>{style}</style>
        <title>{title}</title>
      </head>
      <body>
        <div id="root">
          <QueryClientProvider client={queryClient}>
            <DomainContextProvider domainName={domainName}>
              <PostHogProvider client={posthog}>
                <DebugOverlayProvider>
                  <Layout>
                    <Outlet />
                  </Layout>
                </DebugOverlayProvider>
              </PostHogProvider>
            </DomainContextProvider>
          </QueryClientProvider>

          <ReactQueryDevtools client={queryClient} />
        </div>
        {/* <Scripts /> */}
        <ServicesSSR domain={domain} staticData={staticData} />
      </body>
    </html>
  );
}

function ClientRootComponent() {
  const { domainName, domain, queryClient } = Route.useRouteContext();

  const matches = useRouterState({ select: (s) => s.matches });
  const title = HeadManager.getTitle(matches);

  useEffect(() => {
    HeadManager.setTitle(title);
    HeadManager.initializePostHog(domain, posthog);
  }, [title, domain]);

  useEffect(() => {
    (window as any).posthog = posthog;

    if (typeof window !== "undefined" && "modelContext" in navigator) {
      try {
        (navigator as any).modelContext.provideContext({
          tools: [
            {
              name: "list_rooms",
              description: "List all available rooms and suites on the website.",
              inputSchema: {
                type: "object",
                properties: {}
              },
              execute: async () => {
                try {
                  const res = await roomTypeGetRoomTypes({
                    populate: "*"
                  });
                  const rooms = res?.data || [];
                  const results = rooms.map((r: any) => ({
                    id: r.documentId,
                    title: r.title,
                    slug: r.slug,
                    description: r.description,
                    bathrooms: r.bathrooms,
                    room_size: r.room_size,
                    mirai_room_type_id: r.mirai_room_type_id
                  }));
                  return {
                    content: [{ type: "text", text: JSON.stringify(results, null, 2) }]
                  };
                } catch (err: any) {
                  return {
                    content: [{ type: "text", text: `Error listing rooms: ${err.message}` }]
                  };
                }
              }
            },
            {
              name: "get_room_details",
              description: "Get detailed information about a specific room type by its slug, including bedrooms, amenities, and pricing config.",
              inputSchema: {
                type: "object",
                properties: {
                  slug: {
                    type: "string",
                    description: "The unique slug identifier of the room (e.g., 'villa-silvestre', 'deluxe-suite')."
                  }
                },
                required: ["slug"]
              },
              execute: async (args: any) => {
                try {
                  const { slug } = args;
                  if (!slug) {
                    return {
                      content: [{ type: "text", text: "Error: slug parameter is required." }]
                    };
                  }
                  const res = await roomTypeGetRoomTypes({
                    "filters[slug][$eq]": slug,
                    populate: "*"
                  });
                  const room = res?.data?.[0];
                  if (!room) {
                    return {
                      content: [{ type: "text", text: `Room with slug '${slug}' not found.` }]
                    };
                  }
                  return {
                    content: [{ type: "text", text: JSON.stringify(room, null, 2) }]
                  };
                } catch (err: any) {
                  return {
                    content: [{ type: "text", text: `Error getting room details: ${err.message}` }]
                  };
                }
              }
            },
            {
              name: "check_room_rates",
              description: "Calculate booking price for a specific room type given check-in and check-out dates.",
              inputSchema: {
                type: "object",
                properties: {
                  slug: {
                    type: "string",
                    description: "The unique slug identifier of the room (e.g., 'villa-silvestre')."
                  },
                  startDate: {
                    type: "string",
                    description: "The check-in date in YYYY-MM-DD format."
                  },
                  endDate: {
                    type: "string",
                    description: "The check-out date in YYYY-MM-DD format."
                  }
                },
                required: ["slug", "startDate", "endDate"]
              },
              execute: async (args: any) => {
                try {
                  const { slug, startDate, endDate } = args;
                  const res = await roomTypeGetRoomTypes({
                    "filters[slug][$eq]": slug,
                    populate: "*"
                  });
                  const room = res?.data?.[0];
                  if (!room) {
                    return {
                      content: [{ type: "text", text: `Room with slug '${slug}' not found.` }]
                    };
                  }

                  const s = dayjs(startDate);
                  const e = dayjs(endDate);
                  const pricesPerDate = room.mirai_room_type_prices?.dates || {};
                  const pricesMap = new Map();

                  Object.keys(pricesPerDate).forEach((dateStr) => {
                    const dayjsDate = dayjs(dateStr, "DD/MM/YYYY");
                    if (dayjsDate.isBetween(s.subtract(1, "day"), e)) {
                      const price = pricesPerDate[dateStr];
                      pricesMap.set(dayjsDate.toDate(), price);
                    }
                  });

                  const totalPrice = Array.from(pricesMap.values()).reduce((a, b) => a + b, 0);
                  const avgPricePerNight = pricesMap.size > 0 ? totalPrice / pricesMap.size : 0;

                  const bookingUrl = ratesLinkForRoomType(
                    room.hotel?.mirai_hotel_id,
                    room.mirai_room_type_id,
                    s.toDate(),
                    e.diff(s, "day")
                  );

                  return {
                    content: [{
                      type: "text",
                      text: JSON.stringify({
                        roomTitle: room.title,
                        slug: room.slug,
                        startDate,
                        endDate,
                        nights: e.diff(s, "day"),
                        totalPrice,
                        avgPricePerNight,
                        bookingUrl
                      }, null, 2)
                    }]
                  };
                } catch (err: any) {
                  return {
                    content: [{ type: "text", text: `Error checking room rates: ${err.message}` }]
                  };
                }
              }
            },
            {
              name: "share_page",
              description: "Share a page/URL with someone by email.",
              inputSchema: {
                type: "object",
                properties: {
                  recipientName: {
                    type: "string",
                    description: "The name of the recipient."
                  },
                  recipientEmail: {
                    type: "string",
                    description: "The email address of the recipient."
                  },
                  fromName: {
                    type: "string",
                    description: "The name of the sender."
                  },
                  fromEmail: {
                    type: "string",
                    description: "The email address of the sender."
                  },
                  fromMessage: {
                    type: "string",
                    description: "An optional message to include in the sharing email."
                  }
                },
                required: ["recipientName", "recipientEmail", "fromName", "fromEmail"]
              },
              execute: async (args: any) => {
                try {
                  await sharePage(args);
                  return {
                    content: [{ type: "text", text: "Successfully shared page via email." }]
                  };
                } catch (err: any) {
                  return {
                    content: [{ type: "text", text: `Failed to share page: ${err.message}` }]
                  };
                }
              }
            }
          ]
        });
      } catch (e) {
        console.error("WebMCP context registration failed:", e);
      }
    }
  }, [domain, posthog]);

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <DomainContextProvider domainName={domainName}>
          <PostHogProvider client={posthog}>
            <DebugOverlayProvider>
              <Layout>
                <Outlet />
              </Layout>
            </DebugOverlayProvider>
          </PostHogProvider>
        </DomainContextProvider>
      </QueryClientProvider>
      <ReactQueryDevtools client={queryClient} />
    </>
  );
}

async function ServerErrorComponent() {
  const router = useRouterState();
  const posthog = usePostHog();
  useEffect(() => {
    posthog.capture("Website Error", {
      location: router.location,
      serverSide: true,
    });
  });

  const { domainName } = Route.useRouteContext();
  const style = await HeadManager.getCss(domainName);

  return (
    <html lang="en" data-theme="light">
      <head>
        <HeadContent />
        <style>{style}</style>
        <title>Error</title>
      </head>
      <body>
        <div id="root">
          <ErrorPage />
        </div>
        {/* <Scripts /> */}
      </body>
    </html>
  );
}

function ClientErrorComponent() {
  const router = useRouterState();
  const posthog = usePostHog();
  useEffect(() => {
    posthog.capture("Website Error", {
      location: router.location,
      serverSide: false,
    });
  });

  return (
    <>
      <ErrorPage />
    </>
  );
}

